import React, { useState, useRef, useEffect } from 'react';
import { Camera, Edit3, X, Check, AlertCircle } from 'lucide-react';

/**
 * Avatar component with online status indicator, click actions, and upload functionality
 * Based on Origin UI avatar-07 design with enhanced features
 */
export default function Avatar({ 
  src, 
  alt = 'User', 
  fallback = 'U', 
  size = 'md',
  online = false,
  editable = false,
  showStatus = true,
  status = 'online', // online, offline, away, busy
  onClick = null,
  onUpload = null,
  onEdit = null,
  className = '',
  showTooltip = false,
  tooltipText = '',
  shape = 'rounded', // rounded, circle, square
  borderColor = 'transparent',
  borderWidth = '2',
  animation = true
}) {
  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
    '2xl': 'w-20 h-20 text-xl',
    '3xl': 'w-24 h-24 text-2xl'
  };

  const statusSizeClasses = {
    sm: 'size-2',
    md: 'size-3',
    lg: 'size-3.5',
    xl: 'size-4',
    '2xl': 'size-5',
    '3xl': 'size-6'
  };

  const shapeClasses = {
    rounded: 'rounded-lg',
    circle: 'rounded-full',
    square: 'rounded-none'
  };

  const statusColors = {
    online: 'bg-emerald-500',
    offline: 'bg-gray-400',
    away: 'bg-yellow-500',
    busy: 'bg-red-500'
  };

  const borderClasses = {
    transparent: 'border-transparent',
    white: 'border-white',
    gray: 'border-gray-300',
    green: 'border-accent-green',
    blue: 'border-blue-500'
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image valide');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image ne doit pas dépasser 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageError(false);
        if (onUpload) {
          onUpload(file, e.target.result);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'image:', error);
      alert('Erreur lors du chargement de l\'image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
    } else if (onClick) {
      onClick();
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
  };

  const getInitials = (text) => {
    if (!text) return fallback;
    const words = text.split(' ');
    if (words.length >= 2) {
      return words[0][0].toUpperCase() + words[1][0].toUpperCase();
    }
    return text.substring(0, 2).toUpperCase();
  };

  const avatarContent = (
    <div 
      className={`
        relative inline-block group cursor-pointer transition-all duration-200
        ${animation ? 'hover:scale-105' : ''}
        ${className}
      `}
      onClick={handleClick}
    >
      {/* Avatar container */}
      <div 
        className={`
          ${sizeClasses[size]} 
          ${shapeClasses[shape]}
          overflow-hidden 
          bg-gradient-to-br from-accent-green to-emerald-600 
          flex items-center justify-center 
          font-semibold text-white 
          shadow-lg
          border-${borderWidth}
          ${borderClasses[borderColor]}
          relative
          ${animation ? 'transition-all duration-300' : ''}
        `}
      >
        {/* Image or fallback */}
        {src && !imageError ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className={`${animation ? 'transition-opacity duration-300' : ''}`}>
            {getInitials(fallback)}
          </span>
        )}

        {/* Edit overlay */}
        {editable && isEditing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            {isUploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleEditClick}
                  className="p-1 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Changer l'image"
                >
                  <Camera size={size === 'sm' ? 12 : size === 'md' ? 14 : 16} className="text-white" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Annuler"
                >
                  <X size={size === 'sm' ? 12 : size === 'md' ? 14 : 16} className="text-white" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Edit button (when not editing) */}
        {editable && !isEditing && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
            <Edit3 size={size === 'sm' ? 12 : size === 'md' ? 14 : 16} className="text-white" />
          </div>
        )}
      </div>

      {/* Status indicator */}
      {showStatus && (
        <span 
          className={`
            absolute -end-1 -top-1 
            ${statusSizeClasses[size]} 
            ${shapeClasses[shape]}
            ${statusColors[status]}
            border-2 border-primary-dark 
            shadow-sm
            ${animation ? 'transition-all duration-200' : ''}
          `}
        >
          <span className="sr-only">{status}</span>
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && tooltipText && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          {tooltipText}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {avatarContent}
      
      {/* Hidden file input for upload */}
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      )}
    </>
  );
}

// Default props for better DX
Avatar.defaultProps = {
  size: 'md',
  online: false,
  editable: false,
  showStatus: true,
  status: 'online',
  shape: 'rounded',
  borderColor: 'transparent',
  borderWidth: '2',
  animation: true,
  showTooltip: false,
  tooltipText: ''
};
